import { useState, forwardRef, Ref, ReactNode } from 'react';
import {
  MenuItem,
  IconButton,
  InputAdornment,
  SxProps,
  Theme,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import StyledTextField from './styles';
import { TextFieldListInterface } from './TextFieldListInterface';

interface TextFieldProps<T extends TextFieldListInterface> {
  title: string;
  required?: boolean;
  helperText?: string;
  error?: boolean;
  initialValue?: string;
  disabled?: boolean;
  multiline?: boolean;
  value?: string;
  list?: Array<T> | null;
  menuItems?: SxProps<Theme> | null;
  type?: string;
  adornmentEnd?: ReactNode | null;
  adornmentStart?: ReactNode | null;
  placeholder?: string;
  hasAdornmentStart?: boolean;
  hasAdornmentEnd?: boolean;
  readOnly?: boolean;
  pattern?: string;
  params?: any;
  maxLength?: number;
  numberOnly?: boolean;
  sx?: SxProps<Theme>;
  onValueChange?: (value: string) => void;
}

const TextField = forwardRef<
  HTMLInputElement,
  TextFieldProps<TextFieldListInterface>
>(
  (
    {
      title,
      required = false,
      helperText,
      error = false,
      initialValue,
      disabled = false,
      multiline = false,
      value,
      list,
      menuItems,
      type = 'text',
      adornmentEnd,
      adornmentStart,
      placeholder = '',
      hasAdornmentStart = false,
      hasAdornmentEnd = false,
      readOnly = false,
      numberOnly = false,
      onValueChange = () => {},
      sx,
      maxLength,
      params,
    }: TextFieldProps<TextFieldListInterface>,
    ref: Ref<any> | undefined
  ) => {
    const [isHidden, setHidden] = useState(
      hasAdornmentEnd || hasAdornmentStart
    );
    const [text, setText] = useState<string>('');

    const handleMaxLength = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (
        numberOnly &&
        !/^\d*$/.test(event.target.value.replace(/[^a-zA-Z0-9 ]/g, ''))
      ) {
        onValueChange(text);
        return;
      }
      if (maxLength && event.target.value.length > maxLength) {
        onValueChange(text);
        return;
      }
      setText(event.target.value);
      onValueChange(event.target.value);
    };

    const handleClickShowPassword = () => {
      setHidden(!isHidden);
    };

    const handleMouseDownPassword = (event: { preventDefault: () => void }) => {
      event.preventDefault();
    };

    return (
      <StyledTextField
        {...params}
        sx={sx}
        slotProps={{
          htmlInput: { maxLength: maxLength },
          input: {
            readOnly: readOnly,
            endAdornment: (hasAdornmentEnd || adornmentEnd) && (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                >
                  {adornmentEnd ? (
                    adornmentEnd
                  ) : isHidden ? (
                    <Visibility />
                  ) : (
                    <VisibilityOff />
                  )}
                </IconButton>
              </InputAdornment>
            ),
            startAdornment: (hasAdornmentStart || adornmentStart) && (
              <InputAdornment position="start">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                >
                  {adornmentStart ? (
                    adornmentStart
                  ) : isHidden ? (
                    <Visibility />
                  ) : (
                    <VisibilityOff />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          },
        }}
        color={'focus'}
        type={isHidden ? 'password' : type}
        label={title}
        variant="outlined"
        required={required}
        helperText={helperText}
        defaultValue={initialValue}
        error={error}
        disabled={disabled}
        multiline={multiline}
        select={list != null}
        value={value}
        size="medium"
        placeholder={placeholder}
        fullWidth
        inputRef={ref}
        onChange={handleMaxLength}
        tabIndex={0}
      >
        {list &&
          list.map((item, index) => (
            <MenuItem key={index} value={item.value} sx={menuItems}>
              {item.title}
            </MenuItem>
          ))}
      </StyledTextField>
    );
  }
);

export default TextField;
